import { NgClass } from "@angular/common";
import { Component, InputSignal, input, Signal, computed, signal } from "@angular/core";
import { EvTooltipModule } from "ev-tooltip";
import { IUnitTotalPhysicalExposures, IPhysicalExposure } from "../key-info-physical-exposure/key-info-physical-exposure.component";
import { EvGridTableModule, ITableLayoutModel, ITableHeader, IEvTableRow, ISortOption, BaseListComponent } from "ev-grid-table";

@Component({
    selector: "physical-exposures",
    templateUrl: "./physical-exposures.component.html",
    styleUrls: ["./physical-exposures.component.scss"],
    imports: [NgClass, EvTooltipModule, EvGridTableModule],
})
export class PhysicalExposuresComponent extends BaseListComponent<IPhysicalExposure> {
    data: InputSignal<IUnitTotalPhysicalExposures> = input<IUnitTotalPhysicalExposures>();
    affected: IPhysicalExposure[] = [];
    accordionExpanded: boolean = false;
    distinctCategoryAndScenarios: distinctCategoryScenario[] = [];
    affectedTableModel: IResponsiveTableModelText;

    cleanedData: Signal<IUnitTotalPhysicalExposures> = computed(() => {
        if (!this.data()) {
            return null;
        }
        return {
            exposures: this.data().exposures.map((v) => {
                v.exposureImpactName = this.getImpactName(v.exposureImpactName);
                v.exposureTypeLevel = v.exposureTypeLevel.includes("faregrad") ? "" : v.exposureTypeLevel;
                return v;
            }),
            totalPhysicalRiskScore: this.data().totalPhysicalRiskScore,
            physicalRiskSubtitle: this.data().physicalRiskSubtitle,
        };
    });

    riskScoreTooltipOpen: boolean = false;
    totalRiskScoreTooltipOpen: boolean = false;
    onlyShowAffected = signal(true);
    maxToShow = input<number>(20);

    protected shownExposures = computed(() => {
        if (this.cleanedData() === null) {
            return [];
        }
        if (this.onlyShowAffected()) {
            return this.cleanedData().exposures.filter((d) => d.isExposed);
        }
        return this.cleanedData().exposures;
    });

    originalData = computed(() => this.shownExposures() || []);

    constructor() {
        super();
    }

    showAffectedToggle(): void {
        this.onlyShowAffected.set(!this.onlyShowAffected());
    }

    toggleTotalRiskScoreTooltip(): void {
        this.totalRiskScoreTooltipOpen = !this.totalRiskScoreTooltipOpen;
    }

    closeTotalRiskScoreTooltip(): void {
        this.totalRiskScoreTooltipOpen = false;
    }

    toggleRiskScoreTooltip(e: Event): void {
        e.stopPropagation();
        this.riskScoreTooltipOpen = !this.riskScoreTooltipOpen;
    }

    closeRiskScoreTooltip(): void {
        this.riskScoreTooltipOpen = false;
    }

    private getImpactName(currentImpactString: string): string {
        if (!currentImpactString) {
            return null;
        }
        if (currentImpactString.includes("hovedbygg")) {
            return "Hovedbygning";
        } else if (currentImpactString.includes("bygg")) {
            return "Annen bygning";
        } else if (currentImpactString.includes("tomt")) {
            return "Tomt";
        }
        return null;
    }

    protected createHeaders(): ITableHeader[] {
        return [
            {
                name: "Type",
                prop: "exposureCategoryName",
                desc: false,
                type: "text",
                isSortable: true,
            },
            {
                name: "Scenario",
                prop: "exposureScenarioName",
                desc: false,
                type: "text",
                isSortable: false,
            },
            {
                name: "Nivå",
                prop: "exposureTypeLevel",
                desc: false,
                type: "text",
                isSortable: false,
            },
            {
                name: "Berørt",
                prop: "exposureImpactName",
                desc: true,
                type: "text",
                isSortable: true,
            },
            {
                name: "Risikoscore",
                prop: "exposureRiskScore",
                desc: true,
                type: "number",
                isSortable: true,
                textAlignment: "right",
            },
        ];
    }

    protected createDisplayTemplate(): ITableLayoutModel {
        return {
            breakPoints: [
                {
                    gridAreas:
                        '"exposureCategoryName exposureRiskScore" "exposureScenarioName exposureScenarioName" "exposureImpactName exposureImpactName" "exposureTypeLevel exposureTypeLevel"',
                    gridColumns: "1fr 100px",
                    maxWidth: 550,
                },
                {
                    gridAreas:
                        '"exposureCategoryName exposureScenarioName exposureImpactName exposureRiskScore" "exposureTypeLevel exposureTypeLevel exposureTypeLevel exposureTypeLevel"',
                    gridColumns: "1fr 100px 150px 100px",
                    maxWidth: 750,
                },
                {
                    gridAreas: '"exposureCategoryName exposureScenarioName exposureTypeLevel exposureImpactName exposureRiskScore"',
                    gridColumns: "1fr 100px 1fr 1fr 100px",
                },
            ],
        };
    }
}

export interface distinctCategoryScenario {
    category: string;
    scenario: string;
}

export interface IResponsiveTableModelText {
    rows: IEvTableRow[];
    sortOptions?: ISortOption[];
    selectedSortOption?: ISortOption;
}
